import { Theme } from '@mui/material';
import { CSSObject } from '@mui/material/styles';

/**
 * Customisable emails (PTEC-895): the template editor, the branding panel, the email
 * preview, the test-send dialog and the two pages that host them.
 *
 * Components only set these class names; what they look like lives here, in both
 * palettes. As elsewhere in the theme (`mfa-skip-button`, the upgrade banner, the
 * recommended licence card), classes on a themed MUI component are registered inside
 * that component's own `styleOverrides`, so they sit in the same rule as the theme's
 * glass look and always win over it:
 * - `emailEditorButtonStyles` → `MuiButton`
 * - `emailEditorPaperStyles`  → `MuiPaper`
 * - `emailEditorCardStyles`   → `MuiCard`
 * Everything else (layout boxes, helper text, the preview iframe) has no component
 * override to join, so `emailTemplateEditorStyles` is mounted once as global styles in
 * ThemeContext, scoped by these class names.
 */
export const EMAIL_EDITOR_CLASSES = {
	layout: 'email-editor',
	layoutWithPreview: 'email-editor--with-preview',
	card: 'email-editor__card',
	title: 'email-editor__title',
	lockedTitle: 'email-editor__locked-title',
	hint: 'email-editor__hint',
	placeholderChip: 'email-editor__placeholder-chip',
	revert: 'email-editor__revert',
	helper: 'email-editor__helper',
	helpLinkHeader: 'email-editor__help-link-header',
	textButton: 'email-editor__text-button',
	reloadButton: 'email-editor__reload-button',
	primaryButton: 'email-editor__button--primary',
	secondaryButton: 'email-editor__button--secondary'
} as const;

export const EMAIL_PREVIEW_CLASSES = {
	root: 'email-preview',
	title: 'email-preview__title',
	progress: 'email-preview__progress',
	subject: 'email-preview__subject',
	frame: 'email-preview__frame'
} as const;

export const EMAIL_BRANDING_CLASSES = {
	swatch: 'email-branding__swatch',
	contrast: 'email-branding__contrast',
	contrastPass: 'email-branding__contrast--pass',
	contrastFail: 'email-branding__contrast--fail',
	logo: 'email-branding__logo',
	logoLink: 'email-branding__logo-link'
} as const;

export const EMAIL_TEST_SEND_CLASSES = {
	content: 'email-test-send__content',
	chips: 'email-test-send__chips',
	actions: 'email-test-send__actions'
} as const;

export const EMAIL_PAGE_CLASSES = {
	scroll: 'email-page__scroll',
	toolbar: 'email-page__toolbar',
	description: 'email-page__description',
	descriptionNarrow: 'email-page__description--narrow',
	orgPicker: 'email-page__org-picker',
	loading: 'email-page__loading',
	column: 'email-page__column',
	restricted: 'email-page__restricted',
	restrictedHeader: 'email-page__restricted-header',
	restrictedTitle: 'email-page__restricted-title'
} as const;

const radius = (theme: Theme, multiplier: number) => Number(theme.shape.borderRadius) * multiplier;

// The platform's flat admin buttons (as on the Legal and AI Assistant screens), which
// deliberately escape the theme's global glass button look.
const flatButton = (theme: Theme): CSSObject => ({
	textTransform: 'none',
	paddingTop: theme.spacing(0.75),
	paddingBottom: theme.spacing(0.75),
	borderRadius: radius(theme, 1.5),
	boxShadow: 'none',
	backdropFilter: 'none',
	WebkitBackdropFilter: 'none'
});

export const emailTemplateEditorStyles = (theme: Theme): CSSObject => {
	const { palette } = theme;
	const c = EMAIL_EDITOR_CLASSES;
	const p = EMAIL_PREVIEW_CLASSES;
	const b = EMAIL_BRANDING_CLASSES;
	const ts = EMAIL_TEST_SEND_CLASSES;
	const pg = EMAIL_PAGE_CLASSES;

	return {
		// ── Editor layout: form, and the preview beside it on wide screens ──────────
		[`.${c.layout}`]: {
			display: 'grid',
			gap: theme.spacing(2),
			gridTemplateColumns: '1fr',
			alignItems: 'start'
		},
		[`.${c.layout}.${c.layoutWithPreview}`]: {
			[theme.breakpoints.up('lg')]: {
				gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)'
			}
		},
		[`.MuiTypography-root.${c.title}`]: { fontWeight: 700 },
		[`.MuiTypography-root.${c.lockedTitle}`]: { fontWeight: 600 },
		[`.MuiTypography-root.${c.hint}`]: { marginBottom: theme.spacing(0.75) },
		[`.MuiChip-root.${c.placeholderChip}`]: { fontFamily: 'monospace' },
		[`.MuiIconButton-root.${c.revert}`]: { marginTop: theme.spacing(1) },
		// Field helper text: the message on the left, the character count on the right.
		[`.${c.helper}`]: {
			display: 'flex',
			justifyContent: 'space-between',
			gap: theme.spacing(1)
		},
		[`.${c.helpLinkHeader}`]: { marginBottom: theme.spacing(1) },

		// ── Email preview ───────────────────────────────────────────────────────────
		[`.MuiTypography-root.${p.title}`]: { fontWeight: 700 },
		// Holds the loading bar's height so the frame does not jump when it appears.
		[`.${p.progress}`]: { minHeight: 4 },
		[`.MuiTypography-root.${p.subject}`]: { fontWeight: 600, wordBreak: 'break-word' },
		// An email is always rendered on white, whatever the app's palette.
		[`.${p.frame}`]: {
			width: '100%',
			minHeight: 480,
			border: `1px solid ${palette.divider}`,
			borderRadius: radius(theme, 2),
			backgroundColor: palette.common.white
		},

		// ── Branding panel ──────────────────────────────────────────────────────────
		[`.${b.swatch}`]: { paddingTop: theme.spacing(0.25) },
		[`.${b.contrast}.${b.contrastPass}`]: { color: palette.success.main },
		[`.${b.contrast}.${b.contrastFail}`]: { color: palette.error.main },
		[`.${b.logo}`]: { maxHeight: 64, maxWidth: '100%', objectFit: 'contain' },

		// ── Test-send dialog ────────────────────────────────────────────────────────
		[`.${ts.content}`]: { paddingTop: theme.spacing(0.5) },
		[`.${ts.chips}`]: { marginBottom: theme.spacing(1) },
		[`.MuiDialogActions-root.${ts.actions}`]: {
			paddingLeft: theme.spacing(3),
			paddingRight: theme.spacing(3),
			paddingBottom: theme.spacing(2)
		},

		// ── Pages ───────────────────────────────────────────────────────────────────
		[`.${pg.scroll}`]: { overflowY: 'auto', paddingRight: theme.spacing(0.5) },
		[`.${pg.toolbar}`]: { marginBottom: theme.spacing(2) },
		[`.MuiTypography-root.${pg.description}`]: { maxWidth: 760 },
		[`.MuiTypography-root.${pg.description}.${pg.descriptionNarrow}`]: { maxWidth: 720 },
		[`.MuiAutocomplete-root.${pg.orgPicker}`]: {
			[theme.breakpoints.up('md')]: { minWidth: 300 }
		},
		[`.${pg.loading}`]: {
			display: 'grid',
			placeItems: 'center',
			width: '100%',
			paddingTop: theme.spacing(8),
			paddingBottom: theme.spacing(8)
		},
		[`.${pg.column}`]: {
			display: 'flex',
			flexDirection: 'column',
			gap: theme.spacing(2),
			width: '100%',
			minWidth: 0
		},
		[`.${pg.restrictedHeader}`]: {
			display: 'flex',
			gap: theme.spacing(1.5),
			alignItems: 'center',
			marginBottom: theme.spacing(1)
		},
		[`.MuiTypography-root.${pg.restrictedTitle}`]: { fontWeight: 700 }
	};
};

/** Registered in `MuiButton` (LoadingButton renders a MuiButton too). */
export const emailEditorButtonStyles = (theme: Theme): CSSObject => {
	const { palette } = theme;
	const c = EMAIL_EDITOR_CLASSES;

	return {
		[`&.${c.primaryButton}`]: {
			...flatButton(theme),
			paddingLeft: theme.spacing(2.5),
			paddingRight: theme.spacing(2.5),
			backgroundColor: palette.primary.main,
			color: palette.common.white,
			border: 'none',
			'& .MuiSvgIcon-root': { color: palette.common.white },
			'&:hover': { backgroundColor: palette.primary.dark, boxShadow: 'none', transform: 'none' },
			'&.Mui-disabled': {
				backgroundColor: palette.action.disabledBackground,
				color: palette.action.disabled,
				'& .MuiSvgIcon-root': { color: palette.action.disabled }
			}
		},
		[`&.${c.secondaryButton}`]: {
			...flatButton(theme),
			paddingLeft: theme.spacing(2),
			paddingRight: theme.spacing(2),
			backgroundColor: 'transparent',
			border: `1px solid ${palette.divider}`,
			color: palette.text.secondary,
			'&:hover': {
				backgroundColor: palette.action.hover,
				borderColor: palette.text.secondary,
				boxShadow: 'none',
				transform: 'none'
			}
		},
		[`&.${c.textButton}`]: { textTransform: 'none' },
		[`&.${c.reloadButton}`]: { textTransform: 'none', fontWeight: 700 },
		[`&.${EMAIL_BRANDING_CLASSES.logoLink}`]: { textTransform: 'none', borderRadius: 999 }
	};
};

/** Registered in `MuiPaper`: the editor cards and the preview panel. */
export const emailEditorPaperStyles = (theme: Theme): CSSObject => ({
	[`&.${EMAIL_EDITOR_CLASSES.card}`]: {
		padding: theme.spacing(2),
		borderRadius: radius(theme, 3),
		[theme.breakpoints.up('sm')]: {
			padding: theme.spacing(3)
		}
	},
	[`&.${EMAIL_PREVIEW_CLASSES.root}`]: {
		padding: theme.spacing(2),
		borderRadius: radius(theme, 3),
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(1.5)
	}
});

/** Registered in `MuiCard`: the "organisation admin required" notice. */
export const emailEditorCardStyles = (theme: Theme): CSSObject => ({
	[`&.${EMAIL_PAGE_CLASSES.restricted}`]: {
		padding: theme.spacing(3),
		borderRadius: radius(theme, 3),
		maxWidth: 640
	}
});
